import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BookingDetail,
  BookingState,
  NewBooking,
  PendingReviewBooking,
  UserBookings,
} from './model';
import { BOOKING_ACTIONS, BOOKING_LOADERS } from './utils';
import { Slot } from '../cleaners/model';
import { BookingStatus } from '../chat/model';

const initialState: BookingState = {
  loaders: {},
};

export const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    reset: () => initialState,
    setNewBooking: (state, { payload }: PayloadAction<NewBooking>) => {
      state.newBooking = payload;
    },
    toggleLoader: (
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: BOOKING_LOADERS; value: boolean }>,
    ) => {
      state.loaders[key] = value;
    },
    setUserBookings: (state, { payload }: PayloadAction<UserBookings>) => {
      state.bookings = payload;
    },
    setCurrentBooking: (state, { payload }: PayloadAction<BookingDetail>) => {
      state.currentBooking = payload;
    },
    updateNewBooking: <K extends keyof NewBooking>(
      state,
      { payload }: PayloadAction<{ key: K; value: NewBooking[K] }>,
    ) => {
      const { key, value } = payload;
      state.newBooking[key] = value;
    },
    setBookingSlots: (state, { payload }: PayloadAction<Slot[]>) => {
      if (state.newBooking) {
        state.newBooking.slots = payload;
      }
    },
    removeBooking: (state, { payload }: PayloadAction<string>) => {
      if (state.bookings) {
        state.bookings = state.bookings.filter(({ _id }) => _id !== payload);
      }
    },
    updateCurrentBookingStatus: (
      state,
      { payload }: PayloadAction<BookingStatus>,
    ) => {
      console.log('@@@', payload);
      if (state.currentBooking) {
        state.currentBooking.status = payload;
      }
    },
    updateCurrentBooking: <K extends keyof BookingDetail>(
      state,
      { payload }: PayloadAction<{ key: K; value: BookingDetail[K] }>,
    ) => {
      const { key, value } = payload;
      state.currentBooking[key] = value;
    },
    setPendingReviewBookings: (
      state,
      { payload }: PayloadAction<PendingReviewBooking[]>,
    ) => {
      state.pendingReviewBookings = payload;
    },
  },
});

const extraActions = {
  createNewBooking: createAction<string>(BOOKING_ACTIONS.CREATE_NEW_BOOKING),
  fetchBookings: createAction(BOOKING_ACTIONS.FETCH_BOOKINGS),
  getBookingDetail: createAction<string>(BOOKING_ACTIONS.GET_BOOKING_DETAIL),
  deleteBookingRequest: createAction<{ bookingId: string; fromChat?: boolean }>(
    BOOKING_ACTIONS.DELETE_REQUEST,
  ),
  deleteConfirmedBooking: createAction<string>(
    BOOKING_ACTIONS.DELETE_CONFIRMED_BOOKING,
  ),
  getPendingReviewBookings: createAction(
    BOOKING_ACTIONS.GET_PENDING_REVIEW_BOOKINGS,
  ),
  cleanerUpdateBooking: createAction<{
    bookingId: string;
    messageId?: string;
    newStatus: BookingStatus;
  }>(BOOKING_ACTIONS.CLEANER_UPDATE_BOOKING),
  updateService: createAction<{
    bookingId: string;
    type: 'started' | 'completed';
  }>(BOOKING_ACTIONS.UPDATE_SERVICE),
};

export const {
  reset,
  setNewBooking,
  createNewBooking,
  toggleLoader,
  setPendingReviewBookings,
  fetchBookings,
  setUserBookings,
  getPendingReviewBookings,
  setCurrentBooking,
  updateNewBooking,
  setBookingSlots,
  cleanerUpdateBooking,
  getBookingDetail,
  deleteBookingRequest,
  deleteConfirmedBooking,
  removeBooking,
  updateCurrentBookingStatus,
  updateService,
  updateCurrentBooking,
} = {
  ...bookingsSlice.actions,
  ...extraActions,
};

export const bookingsReducer = bookingsSlice.reducer;
